import React, {useState, useEffect, useCallback} from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/accessibility';

const ElectionChart = ({
                    endpoint,
                    fallbackData,
                    plotOptions = {},
                    seriesConfig = [],
                    title = '',
                    height = null,
                    showSeparators = false,
                    showOnlyHighest,
                    activeTab
                  }) => {
  const [chartOptions, setChartOptions] = useState(null);

  const prepareChartData = useCallback((data, districtName) => {
    const categories = data.map(item => item.name);

    const allValuesZero = data.length > 0 && data.every(item =>
      seriesConfig.every(series => {
        const value = item[series.dataKey] || 0;
        return value === 0 || value === '0';
      })
    );

    const backgroundSeries = allValuesZero ? [{
      name: 'Background',
      data: Array(data.length).fill(100), // Fill with 100% height
      color: 'rgba(247, 247, 247, 0.7)',
      enableMouseTracking: false,
      animation: false,
      dataLabels: {
        enabled: false
      },
      showInLegend: false,
      stack: 'background'
    }] : [];

    const defaultPlotOptions = {
      column: {
        pointPadding: 0.1,
        borderWidth: 0,
        groupPadding: 0.1,
        stacking: allValuesZero ? 'normal' : undefined,
        pointWidth: allValuesZero ? 45 : (plotOptions.column?.pointWidth || undefined),
        dataLabels: {
          enabled: true,
          format: '{point.actualValue}',
          filter: {
            property: 'y',
            operator: '>',
            value: 0
          },
          style: {
            color: '#FFFFFF',
            textOutline: 'none',
            fontWeight: '500',
            fontSize: '12px'
          }
        },
        ...(allValuesZero ? {} : plotOptions.column),
      }
    };

    const options = {
      chart: {
        type: 'column',
        height: height,
        style: {
          fontFamily: "'Roboto', Arial, sans-serif"
        }
      },
      legend: {
        enabled: false
      },
      title: {
        text: title,
        align: 'left'
      },
      xAxis: {
        categories: categories,
        labels: {
          enabled: true,
          formatter: function() {
            // return this.value.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '').substring(0, 6)+'..';
            const value = this.value.toUpperCase().trim();
            if (value === 'PANCHAYATH') {
              return 'GRAMA..';
            }
            return value.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '').substring(0, 5)+'..';
          }
        },
        title: { text: '' },
        crosshair: true,
        ...(showSeparators ? {
          plotBands: categories.map((_, index) => {
            if (index === categories.length - 1) return null; // Skip the last one
            return {
              from: index + 0.5,
              to: index + 0.5,
              color: 'rgba(0, 0, 0, 0.09)',
              width: 1,
              zIndex: 5
            };
          }).filter(Boolean) // Remove any null entries
        } : {}),
      },
      yAxis: {
        min: 0,
        max: 100,
        tickInterval: 25,
        title: { text: '' },
        labels: { enabled: false },
        gridLineWidth: showOnlyHighest ? 1 : 0,
        gridLineColor: 'rgba(0, 0, 0, 0.04)'
      },
      tooltip: {
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        formatter: function() {
          const points = this.points || [this];
          let tooltip = this.point.key ? `<span style="font-size:12px;text-transform:uppercase">${this.point.key}</span><table style="margin-top:5px">` : '';

          points.forEach(point => {
            if (point.maxSeries) {
              // For max series, show the series name and 100%
              const isWayanad2020 = this.point.districtName &&
                this.point.districtName.toLowerCase() === 'wayanad' &&
                this.point.activeTab === '2020';

              const displayName = isWayanad2020 ? 'UDF' : (point.seriesName || point.series.name);
              const sColor = isWayanad2020 ? 'rgb(92,147,199)' : point.series.color;
              tooltip += `
          <tr>
            <td style="color:${sColor};padding:0;font-size:13px">${displayName}: </td>
            <td style="padding:0 0 0 5px;font-size:14px"><b>1</b> <span style="font-size:9px">(100%)</span></td>
          </tr>`;
            } else if (point.actualValue > 0 && point.y > 0 && point.series.name) {
              // For regular points
              tooltip += `
          <tr>
            <td style="color:${point.series.color};padding:0;font-size:13px">${point.series.name}: </td>
            <td style="padding:0 0 0 5px;font-size:14px"><b>${point.actualValue}</b> <span style="font-size:9px">(${Math.round(point.y)}%)</span></td>
          </tr>`;
            }
          });

          tooltip += '</table>';
          return tooltip;
        },
        shared: true,
        useHTML: true
      },
      plotOptions: defaultPlotOptions,
      series: [
        ...backgroundSeries,
        ...seriesConfig.map(series => ({
        ...series,
          stack: allValuesZero ? 'foreground' : undefined,
          data: data.map(item => {
            const isSpecialItem = ['CORPORATION', 'DIST. PANCHAYAT', 'DIST. PANCHAYATH'].includes(
              item.name.toUpperCase().trim()
            );

            let yValue, actualValue, barColor;

            if (showOnlyHighest && isSpecialItem) {
              // Find the series with the highest value
              let maxValue = -Infinity;
              let maxSeriesList = [];
              let maxSeries = null;

              seriesConfig.forEach(s => {
                const value = parseInt(item[s.dataKey] || 0, 10);
                if (value > maxValue) {
                  // New maximum found, reset the list
                  maxValue = value;
                  maxSeriesList = [s];
                } else if (value === maxValue) {
                  // Another series with the same maximum value
                  maxSeriesList.push(s);
                }
              });

              if (maxSeriesList.length > 1) {
                // console.log(`Found ${maxSeriesList.length} series with the maximum value of ${maxValue}`);
                // Handle the tie - you could pick the first one or handle all of them
                maxSeries = maxSeriesList[0];  // Or handle all items in maxSeriesList
              } else if (maxSeriesList.length === 1) {
                maxSeries = maxSeriesList[0];
              }

              if (maxSeries && series.dataKey === maxSeries.dataKey) {
                // This is the series with the highest value
                return {
                  y: maxValue === 0 ? 0 : 100,
                  actualValue: '',
                  maxSeries: true,
                  seriesName: series.name,
                  districtName: districtName,  // Add this line
                  activeTab: activeTab,        // Add this line
                  color: maxSeriesList.length > 1 ?
                    (activeTab === '2020' && districtName.toLowerCase() === 'wayanad' ?
                      'rgb(92,147,199)' : '#6B4E9B') :
                    series.color
                };
              } else {
                // For other series in the special category, don't show (set height to 0)
                return {
                  y: 0,
                  actualValue: 0
                };
              }
            } else {
              // Regular item or showOnlyHighest is false
              actualValue = item[series.dataKey] || 0;
              yValue = (actualValue / (item.total || 1)) * 100;
              barColor = allValuesZero ? '#cccccc' : series.color;
            }

            return {
              y: yValue,
              actualValue: actualValue,
              color: barColor
            };
          }),
        animation: {
          duration: 1000
        }
        }))
      ],
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: true,
        screenReaderSection: {
          beforeChartFormat: '<h2>{chartTitle}</h2>' +
            '<div>{chartSubtitle}</div>' +
            '<div>{chartLongdesc}</div>' +
            '<div>{xAxisDescription}</div>' +
            '<div>{yAxisDescription}</div>'
        }
      }
    };

    setChartOptions(options);
  }, [height, plotOptions?.column, seriesConfig, title, showOnlyHighest, showSeparators, activeTab]);

  const fetchData = useCallback(async () => {
    try {
      const now = new Date();
      const timestamp = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      const response = await fetch(`${endpoint}?ver=${timestamp}`);
      const data = await response.json();
      // console.log(data)
      const chartData = data.state || data.district || fallbackData;
      prepareChartData(chartData, data.name);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [endpoint, fallbackData, prepareChartData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!chartOptions) return null;

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      updateArgs={[true, true, true]}
      containerProps={{ style:{ width: '100%' } }}
    />
  );
};

export default ElectionChart;